<template>
  <div :class="formGroupClass">
    <label v-if="labelVisible && label" :class="labelClass">
      {{label}} <span v-if="validations.required && !readOnly" class="text-danger">*</span>
    </label>
    <div v-if="readOnly" :class="controlClass">
      {{value}}
      <span v-if="description" class="help-block">{{description}}</span>
    </div>
    <div v-else :class="controlClass">
      <div v-if="type === 'PASSWORD'">
        <el-tooltip v-if="tooltipPassword" class="item" effect="light" placement="top" popper-class="infront" :manual="true" :value="showPasswordTT" :enterable="false">
          <div slot="content">
            Passwords must contain;<br/>
            <ul>
              <li :class="{'text-success': matches(this.lenp, false)}"><i :class="tickOr(this.lenp, false)"/>6 or more characters</li>
              <li :class="{'text-success': matches(this.lowp, false)}"><i :class="tickOr(this.lowp, false)"/>1 lowercase letter</li>
              <li :class="{'text-success': matches(this.uppp, false)}"><i :class="tickOr(this.uppp, false)"/>1 uppercase letter</li>
              <li :class="{'text-success': matches(this.digp, false)}"><i :class="tickOr(this.digp, false)"/>1 numerial</li>
              <li :class="{'text-success': matches(this.spep, false)}"><i :class="tickOr(this.spep, false)"/>1 special character from !@#$%^&*-$£</li>
              <li :class="{'text-success': matches(this.othp, true)}"><i :class="tickOr(this.othp, true)"/>No other special characters or spaces</li>
            </ul>
          </div>
          <el-input 
            :name="name"
            type="password"
            v-validate="validations"
            :data-vv-scope="'fields'+validationScope"
            :data-vv-as="validateAs ? validateAs : label.toLowerCase()"
            :value="value"
            :class="{'has-error': errors.has('fields' + validationScope + '.' + name)}"
            @input="$emit('input', $event)"
            @focus="showPasswordTT = true"
            @blur="showPasswordTT = false"
            auto-complete="off"
            :placeholder="usePlaceholder">
          </el-input>
        </el-tooltip>
        <el-input
          v-else 
          :name="name"
          type="password"
          v-validate="validations"
          :data-vv-scope="'fields'+validationScope"
          :data-vv-as="validateAs ? validateAs : label.toLowerCase()"
          :value="value"
          :class="{'has-error': errors.has('fields' + validationScope + '.' + name)}"
          @input="$emit('input', $event)"
          auto-complete="off"
          :placeholder="usePlaceholder">
        </el-input>
      </div>
      <el-select
        v-if="type === 'SELECT'"
        :name="name"
        v-validate="validations"
        :data-vv-scope="'fields'+validationScope"
        :data-vv-as="validateAs ? validateAs : label.toLowerCase()"
        v-model="selectedOption"
        placeholder=""
        :clearable="true"
        style="width:100%"
        @change="$emit('input', selectedOption)"
        :class="{'has-error': errors.has('fields' + validationScope + '.' + name)}"
      >
        <el-option
          v-for="opt in applicationOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        />
      </el-select>
      <div v-if="type === 'TEXT' && !refData">
        <div v-if="mask != null" class="el-input" :class="{'has-error': errors.has('fields' + validationScope + '.' + name)}">
          <masked-input 
            :name="name"
            v-validate="validations"
            :data-vv-scope="'fields'+validationScope"
            data-lpignore="true"
            :data-vv-as="validateAs ? validateAs : label.toLowerCase()"
            class="el-input__inner"
            :mask=mask
            :value="value"
            @input="$emit('input', $event)"
            :placeholder="usePlaceholder"
            @blur="checkPreviousVerifications(name, value)"
            :hasPreviousCheck="hasPreviousCheck" />
        </div>
        <el-input
          :name="name"
          v-if="mask == null"
          v-validate="getValidationRule(name)"
          :data-vv-scope="'fields'+validationScope"
          data-lpignore="true"
          :data-vv-as="validateAs ? validateAs : label.toLowerCase()"
          :value="value"
          :class="{'has-error': errors.has('fields' + validationScope + '.' + name)}"
          @input="$emit('input', $event)"
          :placeholder="usePlaceholder"
          @blur="checkPreviousVerifications(name, value)"
          :hasPreviousCheck="hasPreviousCheck">
      </el-input>
      </div>
      <el-select
        :name="name"
        v-if="type === 'TEXT' && refData"
        v-validate="validations"
        :data-vv-scope="'fields'+validationScope"
        data-lpignore="true"
        data-vv-delay="500" 
        :data-vv-as="validateAs ? validateAs : label.toLowerCase()"
        placeholder=""
        filterable
        prefix-icon="el-icon-search" 
        :remote="!refDataValues"
        :remote-method="remoteSearch"
        default-first-option
        :allow-create="!refDataRestricted"
        v-model="selectedOption"
        :loading="loading"
        v-loading="loading"
        element-loading-spinner="qc-spinner"
        :class="{'has-error': errors.has('fields' + validationScope + '.' + name)}"
        @input="$emit('input', $event)"
        class="verification-select">
         <el-option-group v-if="options != null && options.length > 0" :label="'Type to search and select an option below' + (!refDataRestricted ? ', or enter your own above' : '')">
          <el-option
            v-for="item in options"
            :key="item.id"
            :label="item.name"
            :value="item.idAsValue ? item.id : item.name">
          </el-option>
        </el-option-group>
      </el-select>
      <div v-if="type === 'DATE'" class="el-input" :class="{'has-error': errors.has('fields' + validationScope + '.' + name)}">
        <masked-input 
          :name="name"
          v-validate="validations"
          :data-vv-scope="'fields'+validationScope"
          data-lpignore="true"
          :data-vv-as="validateAs ? validateAs : label.toLowerCase()"
          class="el-input__inner"
          :mask="{
            pattern: 'Dd/Mm/Yyyy',
            formatCharacters: {
              'D': {
                validate: char => /[0-3]/.test(char),
                placeholderChar: '-'
              },
              'd': {
                validate: char => /[0-9]/.test(char)
              },
              'M': {
                validate: char => /[0-1]/.test(char),
              },
              'm': {
                validate: char => /[0-9]/.test(char),
              },
              'Y': {
                validate: char => /[1-2]/.test(char)
              },
              'y': {
                validate: char => /[0-9]/.test(char)
              },
            },
          }"
          placeholder="DD/MM/YYYY"
          :value="value"
          @input="$emit('input', $event), $nextTick(() => { checkPreviousVerifications(name, value) })"
          :hasPreviousCheck="hasPreviousCheck"
          />
      </div>
      <div v-if="type === 'INTEGER'" class="el-input" :class="{'has-error': errors.has('fields' + validationScope + '.' + name)}">
        <masked-input 
          :name="name"
          v-validate="validations"
          :data-vv-scope="'fields'+validationScope"
          data-lpignore="true"
          :data-vv-as="validateAs ? validateAs : label.toLowerCase()"
          class="el-input__inner"
          mask="1111" 
          :value="value"
          @input="$emit('input', $event)" />
      </div>
      <span v-if="description" class="help-block">{{description}}</span>
      <span v-if="type === 'DATE'" class="help-block">Please ensure you use DD/MM/YYYY format.</span>
      <small class="text-danger" v-show="errors.has('fields' + validationScope + '.' + name)">
        {{ errors.first('fields' + validationScope + '.' + name) }}
      </small>
    </div>
  </div>
</template>

<script>
  import _ from 'lodash'
  import moment from 'moment'
  import MaskedInput from 'vue-masked-input'
  
  export default {
    name: 'dynamic-input',
    components: {
      MaskedInput 
    },
    inject: ['$validator'],
    props: {
      name: String,
      value: [String, Number],
      label: String,
      labelVisible: {type: Boolean, default: true},
      description: String,
      placeholder: String,
      mask: String,
      usePlaceholder: String,
      controlSize: { type: Number, default: 10 },
      formGroupClass: {type: String, default: 'form-group'},
      tooltipPassword: {type: Boolean, default: false},
      validations: {
        type: Object,
        default:() => {
          return {required: false}
        }
      },
      validationScope: {type: String, default: ''},
      validateAs: {type: String, default: null},
      required: { type: Boolean, default: false },
      readOnly: { type: Boolean, default: false },
      type: { 
        type: String, 
        default: 'TEXT',
        validator: (value) => {
          let acceptedValues = ['TEXT', 'INTEGER', 'DATE', 'PASSWORD', 'SELECT']
          return acceptedValues.indexOf(value) !== -1
        }
      },
      inline: { type: Boolean, default: true },
      refData: { type: Boolean, default: false },
      refDataInstituteId: { type: String, default: null },
      refDataRestricted: { type: Boolean, default: false },
      refDataValues: Array,
      hasPreviousCheck: {
        type: Boolean,
        default: false
      },
      applicationOptions: {
        type: Array,
        default: () => []
      }
    },
    data () {
      return {
        loading: false,
        options: this.refDataValues,
        selectedOption: this.value || null,
        showPasswordTT: false,
        lenp: /^.{6,}$/,
        lowp: /^(.*[a-z].*)$/,
        uppp: /^(.*[A-Z].*)$/,
        digp: /^(.*[0-9].*)$/,
        spep: /^(.*[!@#$%^&*\-$£].*)$/,
        othp: /^[a-zA-Z0-9!@#$%^&*\-$£]*$/,
      }
    },
    mounted() {
    this.$validator.extend('min_age', {
      validate: (value) => {
        const inputDate = moment(value, 'DD/MM/YYYY');
        const currentDate = moment();
        const age = currentDate.diff(inputDate, 'years');
        return age >= 15;
      },
      getMessage: (field) => {
        return 'The ' + field + ' must be at least 15 years old.';
      }
    });
      this.selectedOption = this.value

    },
    computed: {
      computedValidations() {
        return this.validations;
      },
      labelClass() {
        return this.inline ? 'col-sm-' + (12 - this.controlSize) + ' control-label' : ''
      },
      controlClass() {
        var clazz = this.inline ? 'col-sm-' + this.controlSize : ''
        return this.readOnly ? clazz + ' form-control-static' : clazz + ''
      }
    },
    methods: {
      async checkPreviousVerifications(name, value) {
        if(this.$props.hasPreviousCheck) {
          if(name == "firstname") {
            this.$store.state.savedVerificationData.firstName = value
          }
          if(name == "lastname") {
            this.$store.state.savedVerificationData.lastName = value
          }
          if(name === "dob") {
            this.$store.state.savedVerificationData.dob = value
          }
          this.$api.searchVerifications()
        }
      },
      getValidationRule() {
        return this.computedValidations;
      },
      remoteSearch (query) {
          this.loading = true
          this.doSearch(query)
      },
      doSearch:_.debounce(function(query) {
          this.$api.getRefData(this.refDataInstituteId, this.name, query)
            .then(response => {
              this.loading = false
              this.options = response
            })
            .catch(error => {
              console.log("Failed to load regions")
              this.loading = false
            })  
      }, 200),
      tickOr(pattern, nullok) {
        if(this.matches(pattern, nullok)) {
          return {
            'fa': true,
            'fa-check': true
          }
        } else {
          return {
            'fa': true,
            'fa-times': true
          }
        }
      },
      matches(pattern, nullok) {
        if(this.value) {
          var regex = pattern
          var matches = regex.exec(this.value)
          return matches && matches.length > 0
        } else {
          return nullok
        }
      }
    },
    watch: {
      value(val) {
        this.selectedOption = val
      }
    }
  }
</script>
<style scoped>
ul {
  list-style: none;
}

</style>
